import { API_BASE } from "../config/api";
import useAdminSession from "./useAdminSession";
import React, { useState, useCallback } from "react";
import { Link, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import Dashboard from "./Dashboard";


const apiBase = API_BASE;

export default function Admin() {
  const { session, checking, error: sessionError, login, logout: clearSession, retry } = useAdminSession();
  const navigate = useNavigate();
  const logout = useCallback(() => { clearSession(); navigate("/admin/login", { replace: true }); }, [navigate, clearSession]);

  if (checking) return <main className="flex min-h-screen items-center justify-center bg-[#f8f6f1] text-[#263b3e]"><p role="status">Verifica accesso…</p></main>;
  if (sessionError) return <main className="flex min-h-screen flex-col items-center justify-center gap-5 bg-[#f8f6f1] p-6 text-[#263b3e]"><p role="alert">{sessionError}</p><button className="rounded bg-[#263b3e] px-5 py-3 text-white" onClick={retry}>Riprova</button><button className="underline" onClick={logout}>Torna al login</button></main>;
  if (session) return <Dashboard session={session} onLogout={logout} />;

  return (
    <main className="grid min-h-screen grid-cols-1 bg-[#f8f6f1] text-[#263b3e] md:grid-cols-2 [&_:focus-visible]:outline [&_:focus-visible]:outline-[3px] [&_:focus-visible]:outline-offset-[3px] [&_:focus-visible]:outline-[#a27d45]">
      <Helmet>
        <title>Accesso admin | Serena Ferraris</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <section className="flex flex-col justify-between gap-6 bg-[#263b3e] p-7 text-[#f8f6f1] md:gap-16 md:p-[clamp(32px,6vw,88px)]">
        <Link to="/" className="font-[Georgia,serif] text-[26px] text-inherit no-underline">Serena Ferraris</Link>
        <div>
          <p className="text-[11px] font-semibold tracking-[0.2em]">WEDDING PREMIÈRE</p>
          <h1 className="my-3 font-[Georgia,serif] text-[34px] font-normal leading-[1.15] md:my-6 md:text-[clamp(38px,4.5vw,68px)]">Ogni storia,<br /><em className="font-normal text-[#d6c3a5]">il suo momento.</em></h1>
          <p className="hidden max-w-[330px] leading-[1.8] text-[#dce2df] md:block">Lo spazio riservato alla gestione delle vostre première.</p>
        </div>
        <Link to="/" className="text-sm text-inherit underline-offset-[5px]">← Torna al sito</Link>
      </section>
      <section className="flex items-center justify-center px-6 pb-16 pt-10 md:px-7 md:py-12">
        <Routes>
          <Route path="login" element={session ? <Navigate to="/admin" replace /> : <Login onLogin={(value) => { login(value); navigate("/admin", { replace: true }); }} />} />
          <Route path="/" element={session ? (
            <div className="w-full max-w-[400px]">
              <p className="text-[11px] font-semibold tracking-[0.2em]">AREA RISERVATA</p>
              <h2 className="my-[18px] font-[Georgia,serif] text-4xl font-normal leading-[1.2]">Benvenuta/o, {session.user.name}.</h2>
              <p className="leading-[1.7] text-[#606c6b]" role="status">Accesso amministratore effettuato. La gestione degli eventi sarà disponibile qui.</p>
              <button className="mt-7 min-h-[50px] w-full cursor-pointer rounded border-0 bg-[#263b3e] px-5 py-3.5 text-white hover:bg-[#365355] disabled:cursor-wait disabled:opacity-[0.65]" onClick={() => { clearSession(); navigate("/admin/login", { replace: true }); }}>Esci dall’area admin</button>
            </div>
          ) : <Navigate to="/admin/login" replace />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </section>
    </main>
  );
}

export function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const response = await fetch(`${apiBase}/api/users/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      if (!response.ok) {
        throw new Error(response.status === 401 || response.status === 400
          ? "Email o password non corrette."
          : response.status === 429
            ? "Troppi tentativi. Attendi prima di riprovare."
            : "Il servizio non è disponibile. Riprova tra poco.");
      }
      const result = await response.json();
      if (!result.token || !result.data?.user) throw new Error("Risposta del servizio non valida. Riprova.");
      if (result.data.user.role !== "admin") throw new Error("Questo account non è autorizzato ad accedere all’area admin.");
      setPassword("");
      onLogin({ token: result.token, user: result.data.user });
    } catch (failure) {
      setError(failure instanceof TypeError
        ? "Impossibile raggiungere il servizio. Controlla la connessione e riprova."
        : failure instanceof SyntaxError ? "Risposta del servizio non valida. Riprova." : failure.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="w-full max-w-[400px]">
      <p className="text-[11px] font-semibold tracking-[0.2em]">AREA RISERVATA</p>
      <h2 className="my-[18px] font-[Georgia,serif] text-4xl font-normal leading-[1.2]">Accedi al tuo spazio</h2>
      <p className="leading-[1.7] text-[#606c6b]">Inserisci le credenziali del tuo account amministratore.</p>
      <form className="mt-8" onSubmit={submit} aria-busy={busy}>
        <label className="mb-2 mt-[22px] block text-sm" htmlFor="admin-email">Email</label>
        <input className="box-border min-h-[50px] w-full rounded border border-[#aab4ad] bg-white p-3.5 text-[#263b3e]" id="admin-email" type="email" autoComplete="username" required value={email} onChange={(event) => setEmail(event.target.value)} disabled={busy} />
        <label className="mb-2 mt-[22px] block text-sm" htmlFor="admin-password">Password</label>
        <div className="relative">
          <input className="box-border min-h-[50px] w-full rounded border border-[#aab4ad] bg-white p-3.5 text-[#263b3e] pr-24" id="admin-password" type={visible ? "text" : "password"} autoComplete="current-password" required value={password} onChange={(event) => setPassword(event.target.value)} disabled={busy} />
          <button className="absolute right-1 top-1 h-[42px] cursor-pointer border-0 bg-transparent px-3 text-[#263b3e]" type="button" aria-controls="admin-password" aria-pressed={visible} onClick={() => setVisible(!visible)}>{visible ? "Nascondi" : "Mostra"}</button>
        </div>
        {error && <p className="mt-4 text-sm leading-normal text-[#9a302b]" role="alert">{error}</p>}
        <button className="mt-7 min-h-[50px] w-full cursor-pointer rounded border-0 bg-[#263b3e] px-5 py-3.5 text-white hover:bg-[#365355] disabled:cursor-wait disabled:opacity-[0.65]" type="submit" disabled={busy}>{busy ? "Accesso in corso…" : "Accedi →"}</button>
      </form>
    </div>
  );
}




